import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { API, UPLOADS } from "./../../config/api";
export default function House() {
  const navigate = useNavigate();

  const [houses, setHouses] = useState([]);
  const [searchCity, setSearchCity] = useState("");
  const [priceFilter, setPriceFilter] = useState("");
  const [wishlist, setWishlist] = useState([]);
  const [user, setUser] = useState(null);
  const [minRent, setMinRent] = useState("");
  const [maxRent, setMaxRent] = useState("");
  const [searchState, setSearchState] = useState("");
  /* =========================
      1. PAGINATION STATE
     ========================= */
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(40); // Adjust this number as needed

  /* =========================
      2. FETCH HOUSES
     ========================= */
  const fetchHouses = useCallback(async () => {
    try {
      const res = await fetch(
        `${API.LIST_ACTIVE_WORKSPACES}&t=${new Date().getTime()}`,
      );
      const data = await res.json();
      setHouses(data);
    } catch (error) {
      console.error("Fetch Error:", error);
    }
  }, []);

  useEffect(() => {
    fetchHouses();
    const interval = setInterval(fetchHouses, 5000);
    window.addEventListener("focus", fetchHouses);

    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", fetchHouses);
    };
  }, [fetchHouses]);

  /* =========================
      3. LOAD USER & WISHLIST SYNC
     ========================= */
  const loadUserAndWishlist = useCallback(() => {
    const loginUser = JSON.parse(sessionStorage.getItem("user"));
    setUser(loginUser);

    if (loginUser) {
      const savedWishlist =
        JSON.parse(localStorage.getItem(`wishlist_${loginUser.id}`)) || [];
      setWishlist(savedWishlist);
    } else {
      setWishlist([]);
    }
  }, []);

  useEffect(() => {
    loadUserAndWishlist();
    window.addEventListener("cartUpdated", loadUserAndWishlist);
    window.addEventListener("storage", loadUserAndWishlist);

    return () => {
      window.removeEventListener("cartUpdated", loadUserAndWishlist);
      window.removeEventListener("storage", loadUserAndWishlist);
    };
  }, [loadUserAndWishlist]);

  /* =========================
      4. FRONT-END FILTERS
     ========================= */
  const filteredHouses = houses.filter((item) => {
    const matchesCity = item.city
      ?.toLowerCase()
      .includes(searchCity.toLowerCase());
    const matchesState = item.state
      ?.toLowerCase()
      .includes(searchState.toLowerCase());

    let matchesPrice = true;

    const rent = parseInt(item.rent_price);

    // Radio filters
    if (priceFilter === "under5k") matchesPrice = rent < 5000;
    if (priceFilter === "5to8k") matchesPrice = rent >= 5000 && rent <= 8000;
    if (priceFilter === "above8k") matchesPrice = rent > 8000;

    // Custom Min Max Filter
    if (minRent) matchesPrice = matchesPrice && rent >= Number(minRent);
    if (maxRent) matchesPrice = matchesPrice && rent <= Number(maxRent);

    return matchesCity && matchesState && matchesPrice;
  });

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchCity, searchState, priceFilter, itemsPerPage, minRent, maxRent]);

  /* =========================
      5. PAGINATION CALCULATIONS
     ========================= */
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentHouses = filteredHouses.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredHouses.length / itemsPerPage);

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  /* =========================
      6. TOGGLE WISHLIST
     ========================= */
  const toggleWishlist = (house) => {
    if (!user) {
      alert("Please login first");
      return;
    }
    const storageKey = `wishlist_${user.id}`;
    let savedWishlist = JSON.parse(localStorage.getItem(storageKey)) || [];

    const exists = savedWishlist.find(
      (item) => item.id === house.id && item.type === "house",
    );

    if (exists) {
      savedWishlist = savedWishlist.filter(
        (item) => !(item.id === house.id && item.type === "house"),
      );
    } else {
      savedWishlist.push({ ...house, type: "house" });
    }

    localStorage.setItem(storageKey, JSON.stringify(savedWishlist));
    setWishlist(savedWishlist);
    window.dispatchEvent(new Event("cartUpdated"));
  };

  return (
    <div className="shop-page container-fluid py-4">
      <div className="row">
        {/* LEFT FILTER */}
        <div className="col-lg-2 d-none d-lg-block">
          <div className="filter-sidebar shadow-sm p-3 bg-white rounded sticky-top">
            <h5>Filters</h5>
            <div className="filter-box mt-3">
              <div className="mt-3">
                <h6>Custom Rent</h6>

                <input
                  type="number"
                  placeholder="Min Rent"
                  className="form-control mb-2"
                  value={minRent}
                  onChange={(e) => setMinRent(e.target.value)}
                />

                <input
                  type="number"
                  placeholder="Max Rent"
                  className="form-control"
                  value={maxRent}
                  onChange={(e) => setMaxRent(e.target.value)}
                />
              </div>
            </div>

            <div className="filter-box mt-4">
              <h6>City</h6>
              <input
                type="text"
                placeholder="Search City"
                className="form-control"
                value={searchCity}
                onChange={(e) => setSearchCity(e.target.value)}
              />
            </div>

            <div className="filter-box mt-4">
              <h6>State</h6>
              <input
                type="text"
                placeholder="Search State"
                className="form-control"
                value={searchState}
                onChange={(e) => setSearchState(e.target.value)}
              />

              <button
                className="btn btn-sm btn-link p-0 mt-2"
                onClick={() => {
                  setMinRent("");
                  setMaxRent("");
                  setSearchState("");
                  setSearchCity("");
                }}
              >
                Clear All
              </button>
            </div>
          </div>
        </div>
        {/* MOBILE HORIZONTAL FILTER */}
        <div className="d-lg-none mb-3">
          <div className="mobile-filter-bar">
            <input
              type="number"
              placeholder="Min ₹"
              className="mobile-search"
              value={minRent}
              onChange={(e) => setMinRent(e.target.value)}
            />

            <input
              type="number"
              placeholder="Max ₹"
              className="mobile-search"
              value={maxRent}
              onChange={(e) => setMaxRent(e.target.value)}
            />

            <input
              type="text"
              placeholder="Search City"
              className="mobile-search"
              value={searchCity}
              onChange={(e) => setSearchCity(e.target.value)}
            />
            <input
              type="text"
              placeholder="Search State"
              className="mobile-search"
              value={searchState}
              onChange={(e) => setSearchState(e.target.value)}
            />
          </div>
        </div>
        {/* CENTER CONTENT */}
        <div className="col-lg-8">
          <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
            <h2 className="section-title mb-0">Available Work Rooms</h2>

            <div className="d-flex align-items-center gap-3">
              {/* <p className="text-muted small mb-0">
      {filteredHouses.length} Rooms found
    </p> */}

              <div className="d-flex align-items-center gap-2">
                {/* <span className="small text-muted">Show</span> */}

                <select
                  className="form-select form-select-sm"
                  style={{ width: "80px" }}
                  value={itemsPerPage}
                  onChange={(e) => setItemsPerPage(parseInt(e.target.value))}
                >
                  <option value={20}>20</option>
                  <option value={40}>40</option>
                  <option value={60}>60</option>
                  <option value={100}>100</option>
                </select>

                {/* <span className="small text-muted">per page</span> */}
              </div>
            </div>
          </div>

          <div className="designs-grid">
            {currentHouses.length > 0 ? (
              currentHouses.map((item) => {
                const images = item.image ? item.image.split(",") : [];
                const isLiked = wishlist.some(
                  (w) => w.id === item.id && w.type === "house",
                );

                return (
                  <div className="design-card" key={item.id}>
                    <div className="machine-img-container">
                      <img
                        src={`${UPLOADS.WORKSPACE_IMAGE}${images[0]}`}
                        alt="workroom"
                      />
                    </div>

                    <div className="machine-body">
                      <h4 className="machine-name">{item.location}</h4>
                      <div className="d-flex align-items-center justify-content-between mt-2">
                        <div className="price-block">
                          <span className="currency-symbol">₹</span>
                          <span className="amount">
                            {parseInt(item.rent_price).toLocaleString("en-IN")}
                          </span>
                          <span className="rent-unit"> / mo</span>
                        </div>
                        <button
                          className="contact-btn"
                          onClick={() =>
                            navigate(`/houserentDetails/${item.id}`)
                          }
                        >
                          <i className="bi bi-telephone-fill me-1"></i> Contact
                        </button>
                      </div>

                      <div className="spec-row mt-2">
                        <div className="spec-item">
                          <span className="spec-label">Size:</span>
                          <span className="spec-value"> {item.size} </span>
                        </div>
                        <div className="spec-item">
                          <span className="spec-label">Floor:</span>
                          <span className="spec-value"> {item.floor}</span>
                        </div>
                      </div>

                      <div className="location-info">
                        <i className="bi bi-geo-alt-fill"></i>
                        {item.city},{item.state}
                      </div>

                      <div className="machine-footer mt-1">
                        <button
                          className="more-details-btn"
                          onClick={() =>
                            navigate(`/houserentDetails/${item.id}`)
                          }
                        >
                          <i className="bi bi-info-circle me-2"></i> View
                          Details
                        </button>
                        <button
                          className="footer-like-btn"
                          onClick={() => toggleWishlist(item)}
                        >
                          <i
                            className={
                              isLiked
                                ? "bi bi-heart-fill text-danger"
                                : "bi bi-heart"
                            }
                          ></i>
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="text-center w-100 py-5">
                <p className="text-muted">No workspaces found.</p>
              </div>
            )}
          </div>
          <div className="d-lg-none mt-4">
            <div className="mobile-ads p-3 bg-white shadow-sm rounded text-center">
              <h6 className="fw-bold">Sponsored</h6>

              <div className="ad-card mt-2">
                <div className="ad-placeholder">
                  <p className="small text-muted">Your Ad Here</p>
                </div>
                <p className="small mt-2">Premium Threads Sale!</p>
              </div>
            </div>
          </div>
          {/* ================= PAGINATION UI ================= */}
          {totalPages > 1 && (
            <nav className="d-flex justify-content-center mt-5">
              <ul className="pagination custom-pagination">
                <li
                  className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => paginate(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    <i className="bi bi-chevron-left"></i>
                  </button>
                </li>

                {[...Array(totalPages)].map((_, i) => (
                  <li
                    key={i + 1}
                    className={`page-item ${currentPage === i + 1 ? "active" : ""}`}
                  >
                    <button
                      className="page-link"
                      onClick={() => paginate(i + 1)}
                    >
                      {i + 1}
                    </button>
                  </li>
                ))}

                <li
                  className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => paginate(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    <i className="bi bi-chevron-right"></i>
                  </button>
                </li>
              </ul>
            </nav>
          )}
        </div>

        {/* RIGHT ADS */}
        <div className="col-lg-2">
          <div className="ads-sidebar p-3 bg-white shadow-sm rounded text-center sticky-top">
            <h5 className="ads-title">Sponsored</h5>
            <div className="ad-card mt-3">
              <div className="ad-placeholder">
                <p className="small text-muted">Your Ad Here</p>
              </div>
              <p className="small mt-2">Premium Threads Sale!</p>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .custom-pagination .page-link {
          color: #00a699;
          border: 1px solid #dee2e6;
          margin: 0 3px;
          border-radius: 4px;
          font-weight: 500;
        }
        .custom-pagination .page-item.active .page-link {
          background-color: #00a699;
          border-color: #00a699;
          color: white;
        }
        .custom-pagination .page-link:hover {
          background-color: #f1f1f1;
          color: #00847a;
        }
      `}</style>
    </div>
  );
}
